// Shared email helpers: the DenverBBS signature builder (extracted verbatim from
// pages/ContactsNew/components/SendEmailDialog.js so marketing blasts and single
// emails render the identical signature) and outgoing-HTML normalization.
// Keep buildSignature in sync with bbs-backend/src/utils/utils.signature.ts.
import { imageUrl } from '../config/apiUrl';

export const SIGNATURE_MARKER = 'denverbbs-signature-v1';

function formatPhoneForTel(phone) {
  if (!phone) return '';
  const digits = String(phone).replace(/[^\d+]/g, '');
  return digits.startsWith('+') ? digits : `+1${digits}`;
}

// Format a phone number for display as XXX.XXX.XXXX (matching the office line).
function formatPhoneForDisplay(phone) {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length !== 10) return String(phone);
  return `${local.slice(0, 3)}.${local.slice(3, 6)}.${local.slice(6)}`;
}

function getUserTitle(user) {
  if (user?.designation) return user.designation;
  return '';
}

export function buildSignature(user, photoUrl) {
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || '';

  const title = getUserTitle(user);
  const directPhone = user?.contact || '';
  const meetingLink = user?.meetingLink || 'https://scheduler.zoom.us/michael-nuanes';

  const photoRow = photoUrl
    ? `<td style="padding-right:10px;">
        <img src="${photoUrl}" alt="${fullName}" width="80"
          style="display:block; border:0; width:80px; max-width:80px; height:auto; outline:none; text-decoration:none; border-radius:4px;">
      </td>`
    : '';

  const OFFICE_PHONE_DISPLAY = '720.361.1000';
  const OFFICE_PHONE_TEL = '+17203611000';
  const officeSegment = `<img src="https://denverbbs.com/wp-content/uploads/2026/02/72.png" width="10" /> Office: <a href="tel:${OFFICE_PHONE_TEL}" style="color:#15c;">${OFFICE_PHONE_DISPLAY}</a>`;
  const directSegment = directPhone
    ? `<img style="padding-left: 5px;" src="https://denverbbs.com/wp-content/uploads/2026/02/72-1.png" width="10" /> Direct: <a href="tel:${formatPhoneForTel(directPhone)}" style="color:#15c;">${formatPhoneForDisplay(directPhone)}</a>`
    : '';
  const directLine = directSegment
    ? `${officeSegment} <b>|</b> ${directSegment}`
    : officeSegment;

  return `
  <table cellpadding="0" cellspacing="0" data-sig="${SIGNATURE_MARKER}" style="direction:ltr;font-family:Arial,Helvetica,sans-serif;font-size:11pt;color:rgb(0,0,0)">
    <tr>
      <td>
        <table cellpadding="0" cellspacing="0" border="0">
          <tr>
            ${photoRow}
            <td style="vertical-align:middle;">
              <table cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="font-size:11pt; font-weight:bold; font-family:Arial,Helvetica,sans-serif;">
                    ${fullName}
                  </td>
                </tr>
                ${title ? `<tr>
                  <td style="font-style:italic; font-family:Arial,Helvetica,sans-serif; font-size:11pt; padding-top:4px;">
                    ${title}
                  </td>
                </tr>` : ''}
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>

    <tr>
      <td>
        <a href="https://www.denverbbs.com"><img src="https://denverbbs.com/wp-content/uploads/2026/02/20-years.png" width="242"
          style="display:block; width:242px; max-width:242px; height:auto; border:0;"></a>
      </td>
    </tr>

    <tr>
      <td style="padding:4px 0;">
        <a href="https://www.denverbbs.com" style="color:#000; font-family:Arial,Helvetica,sans-serif;font-size:10pt;font-weight: 700;"><i>DenverBBS.com</i></a> <strong>|</strong> <a href="${meetingLink}"
          style="color:#000;font-family:Arial,Helvetica,sans-serif;font-size:10pt;font-weight: 700;">Schedule a meeting</a>
      </td>
    </tr>

    ${(directLine) ? `<tr>
      <td style="padding-top:8px;font-family:&quot;Microsoft YaHei Light&quot;,serif,EmojiFont;font-size:9pt;color:black">
        ${directLine}
      </td>
    </tr>` : ''}

    <tr>
      <td style="padding:3px 0;font-family:&quot;Microsoft YaHei Light&quot;,serif,EmojiFont;font-size:9pt;color:black">
        <img src="https://denverbbs.com/wp-content/uploads/2026/02/72-2.png" width="10" /><a href="https://www.google.com/maps?q=7651+Shaffer+Parkway,+Suite+B2,+Littleton,+Colorado+80127" style="color:#15c;">7651 Shaffer Pkwy, Ste B2, Littleton, CO 80127</a>
      </td>
    </tr>

    <tr>
        <td style="padding:10px 0 15px;">
            <a href="https://www.facebook.com/buisnessbrokerageservices/" style="text-decoration:none; margin-right:3px;">
                <img src="https://denverbbs.com/wp-content/uploads/2026/02/facebook.png" width="22">
            </a>
            <a href="https://www.instagram.com/denverbbs/" style="text-decoration:none; margin-right:3px;">
                <img src="https://denverbbs.com/wp-content/uploads/2026/02/instagram.png" width="24">
            </a>
            <a href="https://www.linkedin.com/company/business-brokerage-services-llc/" style="text-decoration:none; margin-right:3px;">
                <img src="https://denverbbs.com/wp-content/uploads/2026/02/linkedin.png" width="22">
            </a>
            <a href="https://x.com/denverbbs" style="text-decoration:none;">
                <img src="https://denverbbs.com/wp-content/uploads/2026/02/twitter.png" width="22">
            </a>
        </td>
    </tr>

    <tr>
        <td style="font-family:Arial,sans-serif,serif;font-size:8pt;color:rgb(0,0,0)">
            <strong>CONFIDENTIALITY & DISCLAIMER:</strong> This communication is intended only for the named recipient(s) and may contain confidential, proprietary, or privileged information. If received in error, please notify the sender immediately and delete it. Any use, disclosure, copying, or distribution without authorization is prohibited. Inadvertent transmission does not waive privilege or confidentiality. Business Brokerage Services does not provide legal or tax advice.
        </td>
    </tr>
</table>`;
}

export function withSignature(content, user, photoUrl) {
  const base = content || '';
  if (base.includes(SIGNATURE_MARKER)) return base;
  return base + buildSignature(user, photoUrl);
}

export function getSignaturePhotoUrl(user) {
  return user?.photo && user.photo !== 'default.png' ? imageUrl(user.photo) : '';
}

// Gmail strips <style> blocks and applies its own default ~1em margins to every
// <p>, which roughly doubles the paragraph spacing authored in CKEditor (whose
// editor CSS forces p { margin: 0 }). Inline margin:0 on each <p> open tag so
// mail clients render exactly what the composer shows; authored blank lines
// (<p>&nbsp;</p>) are preserved as the visible spacing.
export function normalizeParagraphMargins(html) {
  if (!html) return html || '';
  return html.replace(/<p(\s[^>]*)?>/gi, (match, attrs) => {
    if (!attrs) return '<p style="margin:0;">';
    const styleRe = /style\s*=\s*(['"])([\s\S]*?)\1/i;
    const styleMatch = attrs.match(styleRe);
    if (!styleMatch) return `<p${attrs} style="margin:0;">`;
    const existing = styleMatch[2].trim();
    const merged = existing
      ? `${existing}${existing.endsWith(';') ? '' : ';'}margin:0;`
      : 'margin:0;';
    return `<p${attrs.replace(styleRe, `style=${styleMatch[1]}${merged}${styleMatch[1]}`)}>`;
  });
}
